import {
    Box,
    CircularProgress,
    Typography,
    useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ReactComponent as LeftArrowIcon } from "../../../../assets/svg/LeftArrowIcon.svg";
import { milestoneStatusStyleArray } from "../../config";
import { formattedAmount } from "../../../../utils/CommonFunction";
import moment from "moment";
import BudgetCarousel from "../Milestones/BudgetCarousel";
import { useTheme } from "@emotion/react";
import { getApiData } from "../../../../utils/APIHelper";
import { toast } from "react-toastify";
import styles from "../../styles";

export const getStatusDisplayText = (text) => {
    if (typeof text !== "string") return text;
    const displayText = text
        .replace(/[-_]/g, " ")
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    return displayText;
};

function ActivityDetails() {
    const { id, activityId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const passedStyles = styles(isMobile);

    const [loader, setLoader] = useState(true);
    const [activityDetails, setActivityDetails] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchActivityDetails();
    }, [activityId, id]);

    async function fetchActivityDetails() {
        try {
            setLoader(true);
            setError(null);
            const response = await getApiData(
                `api/project/activity/${activityId}`,
                "GET",
                {}
            );
            if (response.success && response.data) {
                // Map API response to match the expected structure
                // Transform boqs array to match BudgetCarousel expected format
                const mappedBudget = (response.data.boqs || []).map((boq) => ({
                    ...boq,
                    id: boq.boq_id, // Map boq_id to id for BudgetCarousel
                }));

                const mappedData = {
                    ...response.data,
                    amount: response.data.total_cost,
                    budget: mappedBudget,
                    milestone_image: response.data.milestone_image || null,
                };
                setActivityDetails(mappedData);
            } else {
                setError("Failed to fetch activity details");
                toast.error("Failed to load activity details");
            }
        } catch (err) {
            console.error("Error fetching activity details:", err);
            setError(err.message || "Failed to fetch activity details");
            toast.error("Failed to load activity details");
        } finally {
            setLoader(false);
        }
    }

    if (loader) {
        return (
            <center style={passedStyles.header}>
                <CircularProgress />
            </center>
        );
    }

    if (error || !activityDetails) {
        return (
            <center style={passedStyles.header}>
                <Typography color="error">
                    {error || "Activity not found"}
                </Typography>
            </center>
        );
    }

    return (
        <>
            <div style={passedStyles.container}>
                <header style={passedStyles.header}>
                    <div style={passedStyles.headerContent}>
                        <LeftArrowIcon
                            onClick={() => navigate(-1)}
                            style={{ cursor: "pointer" }}
                        />
                        <div
                            style={{
                                ...passedStyles.idText,
                                fontSize: "14px",
                                fontWeight: "500",
                            }}
                        >
                            {activityDetails?.milestone_name}
                        </div>
                    </div>
                </header>

                <div style={passedStyles.subContainer}>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            gap: isMobile && "20px",
                        }}
                    >
                        <div style={{ ...passedStyles.rowFlex, gap: "15px", flex: "1" }}>
                            <Box
                                component="img"
                                src={
                                    activityDetails?.milestone_image
                                        ? activityDetails?.milestone_image?.path
                                        : "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                                }
                                alt="Activity Icon"
                                sx={{
                                    width: 90,
                                    height: 90,
                                    borderRadius: "16px",
                                    marginRight: 1,
                                }}
                            />
                            <div style={passedStyles.columnFlex}>
                                <h2 style={passedStyles.projectHeading}>
                                    {activityDetails?.milestone_name}
                                </h2>
                                <p style={passedStyles.endDate}>
                                    Last Update:{" "}
                                    {activityDetails?.updatedAt
                                        ? moment(activityDetails.updatedAt).format(
                                            "MMM DD, YYYY hh:mm A"
                                        )
                                        : "N/A"}
                                </p>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box
                                        style={{
                                            width: "15px",
                                            height: "15px",
                                            borderRadius: "50%",
                                            backgroundColor:
                                                milestoneStatusStyleArray.find(
                                                    (style) => style.status === activityDetails?.status
                                                )?.backgroundColor || "transparent",
                                            border: "3px solid #e5e5e5",
                                        }}
                                    />
                                    <Typography
                                        style={{
                                            fontSize: "14px",
                                            marginLeft: "8px",
                                            color: "#0D0D0E",
                                        }}
                                    >
                                        {getStatusDisplayText(activityDetails?.status)}
                                    </Typography>
                                </div>
                            </div>
                        </div>

                        <div style={{ ...passedStyles.columnFlex, flex: "1" }}>
                            <p style={passedStyles.idLabel}>Amount</p>
                            <p style={passedStyles.price}>
                                {formattedAmount(activityDetails?.amount || activityDetails?.total_cost)}
                            </p>
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        ...passedStyles.overview_section,
                        margin: "1vh 2vw 0 2vw",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? "20px" : "50px",
                        padding: isMobile && "1rem 1.6rem",
                    }}
                >
                    <div
                        style={{ ...passedStyles.section_1, ...passedStyles.columnFlex }}
                    >
                        <div style={{ ...passedStyles.columnFlex }}>
                            <div style={passedStyles.section_heading}>Description</div>
                            <h3 style={passedStyles.section_sub_heading}>
                                {activityDetails?.description}
                            </h3>
                        </div>
                        {activityDetails?.budget && activityDetails.budget.length > 0 && (
                            <div style={{ ...passedStyles.columnFlex, marginTop: "2vh" }}>
                                <BudgetCarousel
                                    details={activityDetails}
                                    isMobile={isMobile}
                                    passedStyles={passedStyles}
                                />
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            ...passedStyles.section_2,
                            ...passedStyles.columnFlex,
                            gap: "20px",
                        }}
                    >
                        <div style={passedStyles.section_heading}>Details</div>
                        <div style={passedStyles.columnFlex}>
                            <h3 style={passedStyles.section_sub_heading}>Contractor</h3>
                            <div>
                                <Box display="flex" alignItems="center">
                                    {activityDetails?.selected_contractor && (
                                        <Box
                                            component="img"
                                            src={
                                                activityDetails?.selected_contractor?.profile_url ||
                                                "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                                            }
                                            alt="Contractor Icon"
                                            sx={{
                                                width: 35,
                                                height: 35,
                                                borderRadius: "50%",
                                                boxShadow: "0px 4px 2.67px 0px #00000026",
                                                marginRight: 1,
                                            }}
                                        />
                                    )}
                                    {activityDetails?.selected_contractor?.company_name || activityDetails?.selected_contractor || "N/A"}
                                </Box>
                            </div>
                        </div>
                        {activityDetails?.start_date && (
                            <div style={{ ...passedStyles.rowFlex, gap: "10px", justifyContent: "space-between" }}>
                                <h3 style={passedStyles.section_sub_heading}>Start Date</h3>
                                <p style={{ ...passedStyles.section_sub_heading, color: "#0D0D0E" }}>
                                    {moment(activityDetails.start_date).format("MMM DD, YYYY")}
                                </p>
                            </div>
                        )}
                        {activityDetails?.end_date && (
                            <div style={{ ...passedStyles.rowFlex, gap: "10px", justifyContent: "space-between" }}>
                                <h3 style={passedStyles.section_sub_heading}>End Date</h3>
                                <p style={{ ...passedStyles.section_sub_heading, color: "#0D0D0E" }}>
                                    {moment(activityDetails.end_date).format("MMM DD, YYYY")}
                                </p>
                            </div>
                        )}
                        {activityDetails?.scope_name && (
                            <div style={{ ...passedStyles.rowFlex, gap: "10px", justifyContent: "space-between" }}>
                                <h3 style={passedStyles.section_sub_heading}>Scope</h3>
                                <p style={{ ...passedStyles.section_sub_heading, color: "#0D0D0E" }}>
                                    {activityDetails.scope_name}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default ActivityDetails;

